import { supabase } from './client';
import { getCurrentOrgID } from './organization';
import ErrorHandler from '../utils/ErrorHandler';

/**
 * Fetch all notes for a person (RLS enforces private visibility server-side).
 * Returns notes ordered newest-first, with the author's name and avatar info.
 */
export const getNotesByPersonID = async (personID) => {
    const orgID = await getCurrentOrgID();

    const { data, error } = await supabase
        .from('PeopleNotes')
        .select(`
            id,
            content,
            isPrivate,
            visibility,
            smallGroupID,
            eventInstanceID,
            followUpTouchID,
            taskInstanceID,
            createdAt,
            updatedAt,
            createdBy,
            Author:People!PeopleNotes_createdBy_fkey(
                id, firstName, lastName, avatarStorageKey, avatarUpdatedAt
            )
        `)
        .eq('organizationID', orgID)
        .eq('personID', personID)
        .order('createdAt', { ascending: false });

    if (error) {
        ErrorHandler(
            Error(error.message),
            { query: 'getNotesByPersonID', personID, orgID },
            'Something went wrong loading notes'
        );
        return [];
    }

    return data;
};

/**
 * Create a new note for a person.
 *
 * `visibility` is the canonical field ('PUBLIC'|'PRIVATE'|'FOLLOW_UP'); when
 * provided, `isPrivate` is written to match (PUBLIC→false, else true) so old
 * servers/clients keep working — the server-side guard trigger reconciles
 * either way. Optional `followUpTouchID`/`taskInstanceID` link the note to
 * the same underlying interaction/work without copying content.
 * @param {{ personID: string, content: string, isPrivate?: boolean, visibility?: string,
 *   followUpTouchID?: string, taskInstanceID?: string, createdBy: string }} params
 */
export const createNote = async ({ personID, content, isPrivate = false, visibility, followUpTouchID, taskInstanceID, createdBy }) => {
    const orgID = await getCurrentOrgID();

    const insert = { organizationID: orgID, personID, content, isPrivate, createdBy };
    if (visibility) {
        insert.visibility = visibility;
        insert.isPrivate = visibility !== 'PUBLIC';
    }
    if (followUpTouchID) insert.followUpTouchID = followUpTouchID;
    if (taskInstanceID) insert.taskInstanceID = taskInstanceID;

    const { data, error } = await supabase
        .from('PeopleNotes')
        .insert(insert)
        .select(`
            id,
            content,
            isPrivate,
            visibility,
            smallGroupID,
            eventInstanceID,
            followUpTouchID,
            taskInstanceID,
            createdAt,
            updatedAt,
            createdBy,
            Author:People!PeopleNotes_createdBy_fkey(
                id, firstName, lastName, avatarStorageKey, avatarUpdatedAt
            )
        `)
        .single();

    if (error) {
        ErrorHandler(
            Error(error.message),
            { mutation: 'createNote', personID, orgID },
            'Something went wrong saving the note'
        );
        throw new Error();
    }

    return data;
};

/**
 * Update an existing note's content and/or privacy.
 * `visibility` mirrors createNote — when provided, `isPrivate` is written to
 * match so the legacy column stays consistent (server trigger is authoritative).
 * @param {{ id: string, content: string, isPrivate?: boolean, visibility?: string }} params
 */
export const updateNote = async ({ id, content, isPrivate, visibility }) => {
    const update = { content, isPrivate };
    if (visibility) {
        update.visibility = visibility;
        update.isPrivate = visibility !== 'PUBLIC';
    }

    const { data, error } = await supabase
        .from('PeopleNotes')
        .update(update)
        .eq('id', id)
        .select(`
            id,
            content,
            isPrivate,
            visibility,
            smallGroupID,
            eventInstanceID,
            followUpTouchID,
            taskInstanceID,
            createdAt,
            updatedAt,
            createdBy,
            Author:People!PeopleNotes_createdBy_fkey(
                id, firstName, lastName, avatarStorageKey, avatarUpdatedAt
            )
        `)
        .single();

    if (error) {
        ErrorHandler(
            Error(error.message),
            { mutation: 'updateNote', id },
            'Something went wrong updating the note'
        );
        throw new Error();
    }

    return data;
};

/**
 * Delete a note by ID.
 * @param {string} id
 */
export const deleteNote = async (id) => {
    const { error } = await supabase
        .from('PeopleNotes')
        .delete()
        .eq('id', id);

    if (error) {
        ErrorHandler(
            Error(error.message),
            { mutation: 'deleteNote', id },
            'Something went wrong deleting the note'
        );
        throw new Error();
    }
};
